import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Menu, 
  X, 
  ChevronDown,
  Banknote,
  Building2,
  GraduationCap,
  Hammer,
  Heart,
  ShoppingBag,
  Home,
  Users,
  TrendingUp
} from "lucide-react";

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sectors = [
    { name: "Nhalege Capital", path: "/capital", icon: <Banknote className="w-4 h-4" /> },
    { name: "Wealth Foundation", path: "/foundation", icon: <Heart className="w-4 h-4" /> },
    { name: "Media & Education", path: "/media", icon: <GraduationCap className="w-4 h-4" /> },
    { name: "Contracting", path: "/contracting", icon: <Hammer className="w-4 h-4" /> },
    { name: "Real Estate", path: "/real-estate", icon: <Building2 className="w-4 h-4" /> },
    { name: "E-Commerce", path: "/ecommerce", icon: <ShoppingBag className="w-4 h-4" /> },
  ];

  const mainNavItems = [
    { name: "Home", path: "/", icon: <Home className="w-4 h-4" /> },
    { name: "About", path: "/#about", icon: <Users className="w-4 h-4" /> },
    { name: "Invest", path: "/#contact", icon: <TrendingUp className="w-4 h-4" /> },
  ];

  return (
    <motion.nav 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-xl border-b border-yellow-400/20 shadow-2xl shadow-yellow-400/10' 
          : 'bg-black/80 backdrop-blur-lg border-b border-white/10'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Sacred Geometry Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v-40c11.046 0 20 8.954 20 20zM0 0v40c11.046 0 20-8.954 20-20S11.046 0 0 0z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="group">
            <motion.div 
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center group-hover:shadow-lg group-hover:shadow-yellow-400/50 transition-all duration-300">
                <span className="text-black font-bold text-xl">N</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">
                  Nhalege<span className="text-yellow-400">.</span>
                </div>
                <div className="text-xs text-white/60 font-medium tracking-wider uppercase">
                  Master the Game
                </div>
              </div>
            </motion.div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {mainNavItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                    location.pathname === item.path
                      ? "text-yellow-400 bg-yellow-400/10 border border-yellow-400/20"
                      : "text-white/80 hover:text-yellow-400 hover:bg-white/5"
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.name}</span>
                </Link>
              </motion.div>
            ))}

            {/* Sectors Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsSectorsOpen(true)}
              onMouseLeave={() => setIsSectorsOpen(false)}
            >
              <button className="flex items-center space-x-2 px-4 py-2 text-white/80 hover:text-yellow-400 transition-colors duration-300 rounded-lg hover:bg-white/5">
                <span className="font-medium">Sectors</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isSectorsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <motion.div
                className={`absolute top-full left-0 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-yellow-400/20 rounded-xl shadow-2xl shadow-yellow-400/10 ${
                  isSectorsOpen ? 'block' : 'hidden'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: isSectorsOpen ? 1 : 0, y: isSectorsOpen ? 0 : -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="p-2">
                  {sectors.map((sector, index) => (
                    <Link
                      key={sector.path}
                      to={sector.path}
                      className="flex items-center space-x-3 px-4 py-3 text-white/80 hover:text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-all duration-300 group"
                    >
                      <span className="text-yellow-400 group-hover:scale-110 transition-transform duration-300">
                        {sector.icon}
                      </span>
                      <span className="font-medium">{sector.name}</span>
                    </Link>
                  ))}
                </div>
                <div className="border-t border-white/10 p-4">
                  <p className="text-xs text-white/60 text-center">
                    Six divisions. One vision.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-4">
              <Link
                to="/#contact"
                className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold rounded-full hover:shadow-lg hover:shadow-yellow-400/30 transition-all duration-300 hover:scale-105"
              >
                Join Movement
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 rounded-b-xl"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="p-4 space-y-4">
              {mainNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center space-x-3 px-4 py-3 text-white/80 hover:text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-all duration-300"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.icon}
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
              
              <div className="border-t border-white/10 pt-4">
                <p className="text-yellow-400 font-semibold mb-3 px-4">Sectors</p>
                {sectors.map((sector) => (
                  <Link
                    key={sector.path}
                    to={sector.path}
                    className="flex items-center space-x-3 px-6 py-2 text-white/70 hover:text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {sector.icon}
                    <span>{sector.name}</span>
                  </Link>
                ))}
              </div>
              
              <div className="border-t border-white/10 pt-4">
                <Link
                  to="/#contact"
                  className="block w-full text-center px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold rounded-full"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Join Movement
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default NavBar;